/**
 * ChatSelectionContext
 *
 * Carries selected text from the editor to the ChatPanel.
 * Same provider pattern as HelpPanelContext.
 */

import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';

export interface ChatSelectionContextValue {
  selectedText: string | null;
  sourceDocumentId: string | null;
  setSelection: (text: string, documentId?: string) => void;
  clearSelection: () => void;
}

const ChatSelectionContext = createContext<ChatSelectionContextValue | undefined>(undefined);

export const ChatSelectionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedText, setSelectedText] = useState<string | null>(null);
  const [sourceDocumentId, setSourceDocumentId] = useState<string | null>(null);

  const setSelection = useCallback((text: string, documentId?: string) => {
    setSelectedText(text);
    setSourceDocumentId(documentId ?? null);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedText(null);
    setSourceDocumentId(null);
  }, []);

  const value = useMemo<ChatSelectionContextValue>(
    () => ({ selectedText, sourceDocumentId, setSelection, clearSelection }),
    [selectedText, sourceDocumentId, setSelection, clearSelection],
  );

  return (
    <ChatSelectionContext.Provider value={value}>
      {children}
    </ChatSelectionContext.Provider>
  );
};

export function useChatSelection(): ChatSelectionContextValue {
  const context = useContext(ChatSelectionContext);
  if (!context) {
    throw new Error('useChatSelection must be used within a ChatSelectionProvider');
  }
  return context;
}
